/*
  FULL FILE REPLACEMENT for: assets/custom-upsell-products.js

  WHAT CHANGED AND WHY
  --------------------
  Only one thing changed: a new applyCustomHeading() method, called immediately
  after replaceChildren() inside fetchRecommendations().

  Why it is needed:
  The upsell list is re-rendered by fetching
    /recommendations/products?section_id=section-upsell-products
  In that render context Shopify exposes only the `recommendations` object, so
  Liquid there cannot read product.metafields.custom.upsell_heading_option.
  The heading is therefore resolved on the PDP render and stamped onto this
  element as data-heading. replaceChildren() swaps the inner html but leaves the
  host element and its attributes untouched, so data-heading survives the swap.

  Safe by default: if data-heading is absent or empty we leave the markup alone,
  so products without the metafield keep the existing translated heading.
*/

class CustomUpsellProducts extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.instantAdd = false;

    if (this.dataset.productId) {
      this.instantAdd = true;
      this.fetchRecommendations(this.dataset.productId, this.dataset.intent);
    }

    this.setupEventListeners();
    this.addObserver();
  }

  setupEventListeners() {
    this.addEventListener("upsell:add", (event) => {
      if (this.instantAdd) {
        this.addToCart(event.detail);
        return;
      }

      this.closest("flexa-converter-summary").dispatchEvent(
        new CustomEvent("upsell:add", { detail: event.detail })
      );
    });
  }

  addObserver() {
    const mutationCallback = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type !== "attributes" || mutation.attributeName !== "data-product-id") {
          return;
        }

        this.fetchRecommendations(
          mutation.target.getAttribute("data-product-id"),
          this.dataset.intent
        );
      }
    };

    const observer = new MutationObserver(mutationCallback);
    observer.observe(this, { attributes: true });
  }

  /*
    NEW: overwrite the generic heading with the per-product one.
    Reads data-heading off this element (set by the Liquid snippet on the PDP
    render, resolved from custom.upsell_heading_option). No-op when absent, so
    the theme's existing related/complementary title stays untouched.
  */
  applyCustomHeading() {
    const heading = (this.dataset.heading || "").trim();
    if (!heading) return;

    // data-upsell-title is the hook added in custom-upsell-products.liquid;
    // the class is kept as a fallback in case the markup is edited later.
    const titleElement =
      this.querySelector("[data-upsell-title]") ||
      this.querySelector(".upsell-products__title");

    if (titleElement) {
      titleElement.textContent = heading;
    }
  }

  fetchRecommendations(productId, intent = "related", callback = null) {
    fetch(
      `${window.Shopify.routes.root}recommendations/products?product_id=${productId}&limit=5&section_id=section-upsell-products&intent=${intent}`
    )
      .then((response) => response.text())
      .then((text) => {
        const parser = new DOMParser();
        const html = parser.parseFromString(text, "text/html");
        const recommendations = html.querySelector("custom-upsell-products");

        if (callback) callback();

        if (recommendations && recommendations.innerHTML.trim().length) {
          this.replaceChildren(...recommendations.children);
          // Must run after replaceChildren, the h3 does not exist before this.
          this.applyCustomHeading();
        }
      });
  }

  addToCart(upsellElement) {
    const formData = {
      items: [
        {
          id: upsellElement.dataset.variantId,
          quantity: 1,
        },
      ],
    };

    fetch(window.Shopify.routes.root + "cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    })
      .then((response) => {
        document.dispatchEvent(new Event("theme:cart:refresh"));
        this.fetchRecommendations(this.dataset.productId, this.dataset.intent, () => upsellElement.remove());
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  }
}

customElements.define("custom-upsell-products", CustomUpsellProducts);

class CustomUpsellProduct extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.addEventListener("click", (event) => {
      this.classList.toggle("custom-upsell-product--selected");

      this.closest("custom-upsell-products").dispatchEvent(
        new CustomEvent("upsell:add", { detail: this })
      );
    });
  }
}

customElements.define("custom-upsell-product", CustomUpsellProduct);
